import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Dialog, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from "@mui/icons-material/TextSnippet";

import AnnulledStamp from "./AnnulledStamp";

const title = {
  internal: "COMUNICACIÓN INTERNA",
  external: "COMUNICACIÓN DESPACHADA",
  received: "COMUNICACIÓN RECIBIDA",
};

// `match` is only supplied by the radicados lists while searching, and only
// when the hit came from the document's OCR text — a hit on the asunto or the
// remitente is already visible on the card and needs no quote.
const FiledItem = ({ item, match }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const cancelReason = item.cancellation?.reason || item.cancelledReason;

  const handleClick = () => {
    if (item.status === "DONE") {
      navigate(`/filed/view/${item.referenceId}`);
    } else if (item.status === "CANCELLED") {
      navigate(`/filed/cancelled/${item.filedId}`);
    } else if (item.status !== "PDF") {
      navigate(`/filed/${item.filedId}`);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <img alt="Filed preview" src={item.thumbUrl} width={520} style={{ maxWidth: '100%' }} />
      </Dialog>
      <Box
        className="box-filed-item"
        xs={4}
        sx={{ p: 1, position: "relative" }}
        onClick={handleClick}
      >
        <Box
          sx={{
            border: "thin solid lightgray",
            borderRadius: 1,
            boxShadow: "0px 0px 2px #ccc",
            display: "flex",
            flexDirection: "row",
            fontSize: "12px",
            // minHeight, not height: the OCR match snippet has to be able to
            // grow the card. The wrapper already wraps, so mixed heights are fine.
            minHeight: 120,
            p: 1,
            position: "relative",
          }}
        >
          {item.status === "CANCELLED" && <AnnulledStamp />}
          <Box sx={{ width: 100, height: 120, backgroundColor: 'lightgray', border: 'thin dashed gray' }}>
            <img
              alt="Filed preview"
              src={item.thumbUrl || '/pdf.png'}
              height={120}
              style={{ backgroundColor: 'white', maxWidth: 90, objectFit: 'contain' }}
              onClick={(evt) => {
                if (!item.thumbUrl) return;
                evt.stopPropagation();
                setIsOpen(true);
              }}
            />
          </Box>
          <Box
            sx={{
              ml: 1,
              width: "100%",
              // Without it the one-line motive widens the column past the card
              // instead of truncating, and pushes the number off the edge.
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex" }}>
              <Box sx={{ flex: 1 }}>{title[item.filedType] || "RADICADO"}</Box>
              <Box>{`No. ${item.filedId}`}</Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              {item.subject?.substr(0, 200)}{item.subject && item.subject.length > 200 && '...'}
            </Box>
            {match && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, my: 0.5 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  icon={<TextSnippetIcon />}
                  label="En el documento"
                  sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontStyle: "italic",
                    lineHeight: 1.2,
                  }}
                >
                  {match.leading && <span>…</span>}{match.before}
                  <Box component="mark" sx={{ bgcolor: "warning.light", px: 0.2 }}>
                    {match.match}
                  </Box>
                  {match.after}{match.trailing && <span>…</span>}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex" }}>
              <Box sx={{ flex: 1, alignItems: "flex-start" }}>
                {item.createdAt.format("LL, h:mm:ss a")}
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                {`No. folios ${item.folios}`}
                {item.status === "DONE" && (
                  <LockIcon sx={{ ml: 1 }} fontSize="10" />
                )}
                {item.status === "PDF" && (
                  <Box sx={{
                    alignItems: 'center',
                    display: 'flex',
                    position: 'absolute',
                    justifyContent: 'center',
                    top: '40%',
                    left: '40%',
                  }}>
                    <span>Construyendo...</span>
                    <PictureAsPdfIcon sx={{ ml: 1, color: 'red' }} />
                  </Box>
                )}
              </Box>
            </Box>
            {/* An annulled card keeps its details under the stamp: the number
                alone didn't tell anyone which radicado it was. */}
            {cancelReason && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {`Motivo de anulación: ${cancelReason}`}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FiledItem;
